import { useState } from "react";
import { Box, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import BaseButton from "../../BaseButton";
import BaseRadio from "../../BaseRadio";

export default function BrainMemoryConsentModal({ close, data }) {
  const { onConfirm = () => {} } = data
  const [isUnderstand, setIsUnderstand] = useState('')

  const handleClickConfirm = async () => {
    if (onConfirm) {
      await onConfirm()
    }
    close()
  }

  return (
    <Box sx={{
      width: {  lg: '1000px' },
      borderRadius: '16px',
      boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.04)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'white',
      height: '90vh'
    }}>
      <Box
        sx={{
          px: '20px',
          height: { xs: '400px', md: '700px' },
          overflowY: 'auto',
        }}
        className='notification-dropdown-scroll'
      >
        <Box
          sx={{
            mt: '24px'
          }}
        >
          <Box
            sx={{
              padding: '4px 0',
              borderRadius: '4px',
              bgcolor: 'background.default'
            }}
          >
            <Typography variant="h3" color='primary.red'>
              Before accessing the Brain & Memory report, please carefully review the following information and confirm your patient's consent: 
            </Typography>
          </Box><br />
          <Typography variant="h3" sx={{ textDecoration: 'underline' }}>
            dDNA Brain & Memory Report Results: Important Information
          </Typography>
          <Typography variant="h4">
            Over 60 genes associated with neurological conditions such as Parkinson's disease, Alzheimer's disease and ALS are included on the Brain & Memory report. Many of the included disease-causing variants are rare and are also usually seen in the context of a known positive family history. That said, variants in these genes may be associated with reduced penetrance (meaning not all people with the positive variant will develop the condition) and/or variable expression (meaning the age of onset and disease presentation can vary among individuals). Of note, not all gene variants on this panel are rare (for example, the APOE gene is included, please see details below).
            <br /><br />
          </Typography>
          <Typography variant="h3" sx={{ textDecoration: 'underline' }}>
            Potential Implications of Results
          </Typography>
          <Typography variant="h4">
            Receiving these results may:
            <ul>
              <li>Cause anxiety or distress</li>
              <li>Influence major life decisions (e.g., financial planning, career choices, family planning)</li>
              <li>Have implications for family members</li>
              <li>Potentially impact insurance coverage (depending on legal protections in your jurisdiction)</li>
            </ul>
          </Typography>
          <Typography variant="h3" sx={{ textDecoration: 'underline' }}>
            About APOE Testing and Alzheimer's Disease
          </Typography>
          <Typography variant="h4">
            Certain COMMON variants in the APOE gene are associated with an increased risk of developing Alzheimer's disease. However, it's important to understand:
            <ol>
              <li>This test does not diagnose Alzheimer's disease.</li>
              <li>A higher-risk result does not guarantee that an individual will develop Alzheimer's.</li>
              <li>A lower-risk result does not guarantee that an individual will not develop Alzheimer's.</li>
            </ol>
          </Typography>
          <Typography variant="h3" sx={{ textDecoration: 'underline' }}>
            Current State of Alzheimer's Treatment and Prevention
          </Typography>
          <Typography variant="h4">
            It's important that your patient understands:
            <ul>
              <li>There is currently no cure for Alzheimer's disease.</li>
              <li>There is no surefire way to prevent Alzheimer's disease.</li>
              <li>Some lifestyle factors may influence risk, but they do not guarantee prevention.</li>
            </ul>
          </Typography>
          <Typography variant="h3" sx={{ textDecoration: 'underline' }}>
            Consent Confirmation 
          </Typography>
          <Typography variant="h4">
            Has your patient provided informed consent to receive results concerning:
            <ul>
              <li>Genetic risk factors (both common and rare variants ) for Alzheimer's disease</li>
              <li>Genetic risk factors for other neurological conditions</li>
            </ul>
          </Typography>
          <Typography variant="h3" sx={{ textDecoration: 'underline' }}>
            Please ensure your patient understands:
          </Typography>
          <Typography variant="h4">
            <ul>
              <li>The significance of the test for APOE alleles</li>
              <li>The limitations of the information provided</li>
              <li>The potential psychological impact of the results</li>
              <li>The current lack of definitive prevention or cure for Alzheimer's disease and other neurological conditions</li>
            </ul>
          </Typography>
          <Box>
            <BaseRadio
              label='Yes, I confirm that the patient has given informed consent to receive these results, understanding the above information.'
              checked={isUnderstand === 'Y'}
              onChange={() => setIsUnderstand('Y')}
              name="radio-buttons"
              sxLabel={{
                fontSize: '17px',
                color: 'primary.darkBlack',
                fontWeight: '600',
                whiteSpace: 'normal',
                ml: '10px'
              }}
              sx={{
                fontSize: '20px'
              }}
            /><br />
            <BaseRadio
              label='No, the patient has not given informed consent OR I need to further discuss these points with the patient.'
              checked={isUnderstand === 'N'}
              onChange={() => setIsUnderstand('N')}
              name="radio-buttons"
              sxLabel={{
                fontSize: '17px',
                color: 'primary.darkBlack',
                fontWeight: '600',
                whiteSpace: 'normal',
                ml: '10px'
              }}
              sx={{
                fontSize: '20px'
              }}
            />
          </Box>
          <br />
          <Typography variant="h4" color='primary.red'>
            (Please select one option to continue)
          </Typography><br />
          <Typography variant="h4">
            If you need additional resources to discuss this with your patient, please contact our genetic counseling team at 281-819-6574.
          </Typography><br />
        </Box>
      </Box>
      <Box sx={{
        bgcolor: 'primary.darkBlack',
        borderTop: '1px solid #CCCCCC',
        borderRadius: '0 0 16px 16px',
        width: '100%',
        padding: '8px',
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <BaseButton
          width='120px'
          buttonType="info"
          height={'36px'}
          onClick={close}
        >
          <CloseIcon />
          <span>Cancel</span>
        </BaseButton>
        <BaseButton
          width='170px'
          buttonType="primary"
          height={'36px'}
          onClick={handleClickConfirm}
          disabled={isUnderstand !== 'Y'}
        >
          <span>I understand</span>
          <ArrowForwardIosIcon fontSize="30px" sx={{ ml: '8px' }} />
        </BaseButton>
      </Box>
    </Box>
  )
}